/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {z} from 'zod'
import {useTranslation} from 'react-i18next'
import {useMemo} from 'react'

export const useCompanyAddressSchema = () => {
    const { t } = useTranslation()

    return useMemo(
        () =>
            z.object({
                name: z
                    .string()
                    .trim()
                    .min(2, t('Name must be at least 2 characters'))
                    .max(100, t('Name must be at most 100 characters'))
                    .nullable(),
                department_name: z
                    .string()
                    .trim()
                    .max(100, t('Department name must be at most 100 characters'))
                    .optional()
                    .nullable(),
                street_name: z
                    .string()
                    .trim()
                    .min(2, t('Street must be at least 2 characters'))
                    .max(120, t('Street must be at most 120 characters')),
                postal_code: z
                    .string()
                    .trim()
                    .regex(/^[A-Za-z0-9\- ]{3,10}$/u, t('Invalid postal code format')),
                town: z
                    .string()
                    .trim()
                    .min(2, t('Town must be at least 2 characters'))
                    .max(80, t('Town must be at most 80 characters')),
                region: z
                    .string()
                    .trim()
                    .min(2, t('Region must be at least 2 characters'))
                    .max(80, t('Region must be at most 80 characters')),
                country: z
                    .string()
                    .trim()
                    .min(2, t('Country must be at least 2 characters'))
                    .max(80, t('Country must be at most 80 characters')),
                note: z.string().trim().max(500, t('Note must be at most 500 characters')).optional().nullable(),
            }),
        [t]
    )
}

export type CompanyAddressInputs = z.infer<ReturnType<typeof useCompanyAddressSchema>>
